import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import templateApi from '../../APIs/template';
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Typography,
} from '../../designComponents';
import { TableDataType } from '../../designComponents/Table';
import { ADD_TEMPLATE_ROUTE } from '../../routes/routePaths';
import Store from '../../store';
import { RootState } from '../../store/selectors';
import { PaginationType } from '../../types';
import { humanReadableDate, requestAPI } from '../../utils';
import { PrinterContainerStyled } from './styles';

type MyParamsType = PaginationType & { search?: string };
type ParamsType = {
  page: number;
  limit: number;
};

const Template = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const handleAddTemplate = () => navigate(ADD_TEMPLATE_ROUTE);

  const templateData = useSelector((state: RootState) => state.templates.data);
  const pagination = useSelector(
    (state: RootState) => state.templates.pagination
  );

  const getTemplateData = async (params: MyParamsType) => {
    try {
      await requestAPI(
        templateApi.getAllTemplate({
          page: params.page,
          limit: params.limit,
          search: params.search ? params.search : '',
        })
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getTemplateData(pagination);
  }, []);

  const tableData: TableDataType = {
    headings: [
      {
        key: 'date',
        label: 'Created Date',
      },
      {
        key: 'title',
        label: 'Template Title',
      },
      {
        key: 'country',
        label: 'Template Country',
      },
      {
        key: 'templateFiles',
        label: 'Template File',
      },
    ],
    body: Array.isArray(templateData)
      ? templateData?.map((template) => {
          return {
            id: template._id,
            date: humanReadableDate(template?.createdAt),
            title: template?.templateTitle,
            country: template?.country,
            templateFiles: (
              <img
                src={template?.uploadTemplate[0]}
                height={'40px'}
                width={'40px'}
                style={{ objectFit: 'cover' }}
                alt="template"
              />
            ),
          };
        })
      : [],
  };

  const debouncedFunction = useCallback(
    debounce((myParams) => getTemplateData(myParams), 500),
    []
  );

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedFunction({ ...pagination, page: 1, search: value });
  };

  const handlePagination = (updatedParams: ParamsType) => {
    getTemplateData(updatedParams);
    dispatch(Store.Actions.update('templates.pagination', updatedParams));
  };

  return (
    <PrinterContainerStyled>
      <PageHeader
        pageTitle={<Typography variant="title">Template File</Typography>}
        otherActions={
          <Button variant="secondary" size="sm" onClick={handleAddTemplate}>
            <FaPlus /> Add Template
          </Button>
        }
        handleOnSearchTextChange={handleOnSearchTextChange}
        search={search}
      />

      <div className="table-container">
        <Table
          tableData={tableData}
          isLoading={undefined}
          maxWidthForCardView="800px"
        />
      </div>
      <Pagination
        activePage={pagination.page}
        handlePagination={handlePagination}
        limit={pagination.limit}
        totalCount={pagination.totalData || 0}
      />
    </PrinterContainerStyled>
  );
};

export default Template;
